import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Address } from '@/types/gearApi/Address';
import AddressDictionary from '@/types/dictionaries/Address';
import AddressSelectionDictionary from '@/types/dictionaries/AddressSelection';
import AddressSelectionDialog from '@/components/AddressSelectionDialog';
import { logServerError } from '@/utils/helpers/logHelper';
import { ROUTES } from '@/constants/application-urls';
import { getErrorMessage } from '@/utils/helpers/errorHelper';
import { useAccountDataStore } from '@/utils/providers/AccountDataStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { NormalizedLocale } from '@/enums/Locale';

interface BillingProfileDialogProps {
	isOpen: boolean;
	onClose: () => void;
	dict: { addressCard: AddressDictionary; addressSelection: AddressSelectionDictionary };
	forceUserToSelectBillingProfile: boolean;
}

export default function BillingProfileDialog({
	isOpen,
	onClose,
	dict,
	forceUserToSelectBillingProfile,
}: Readonly<BillingProfileDialogProps>) {
	const { addresses, preselectedAddress, setSelectedBillingProfile } = useAccountDataStore(
		useShallow((state) => ({
			addresses: state.billingProfiles,
			preselectedAddress: state.selectedBillingProfile,
			setSelectedBillingProfile: state.setSelectedBillingProfile,
		})),
	);

	const path = usePathname();
	const locales = Object.values(NormalizedLocale);
	const isHomePath = locales.some((locale) => path.endsWith(locale));

	const { update } = useSession();
	const router = useRouter();
	const handleContinue = async (selectedAddress: Address) => {
		try {
			await update({ newBusinessUnitKey: selectedAddress.addressContact.businessUnitKey });
			setSelectedBillingProfile(selectedAddress);
			if (forceUserToSelectBillingProfile && !isHomePath) {
				router.push(ROUTES.HOME);
			} else {
				router.refresh();
			}
			onClose();
		} catch (e) {
			logServerError(`Failed to set selected business unit because of ${getErrorMessage(e)}`);
			throw new Error(dict.addressSelection.error);
		}
	};

	return (
		<AddressSelectionDialog
			addresses={addresses}
			preselectedAddress={preselectedAddress}
			dict={dict}
			onClose={onClose}
			onContinue={handleContinue}
			isOpen={isOpen}
			isBillingProfileSelection
			forceUserToSelectAnAddress={forceUserToSelectBillingProfile}
		/>
	);
}
